import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { IBasketItem } from '@/context/BasketStore'

export interface DiscountCode {
    emailBound?: boolean
    signupEmail?: string
    code: string
    valid: boolean
    discountType: 'percentage' | 'fixed' | 'free_addon' | 'free_delivery'
    discountValue: number
    isLoading: boolean
    error: string | null
    label?: string
    freeAddonIds?: string[] // Product IDs that should be made free
    freeAddonTypes?: string[] // Product catalogue IDs that should be made free
}

interface DiscountStore {
    discountCode: DiscountCode | null
    setDiscountCode: (code: string) => void
    setDiscountValidation: (validation: Partial<DiscountCode>) => void
    clearDiscount: () => void
    calculateDiscountAmount: (subtotal: number) => number
    calculateFreeAddonDiscount: (basketItems: IBasketItem[]) => number
    calculateFreeAddonSavings: (basketItems: IBasketItem[]) => number
    isAddonFree: (addonId: string, catalogueId?: string) => boolean
}

const useDiscountStore = create<DiscountStore>()(
    persist(
        (set, get) => ({
            discountCode: null,

            setDiscountCode: (code: string) => {
                set({
                    discountCode: {
                        code,
                        valid: false,
                        discountType: 'percentage',
                        discountValue: 0,
                        isLoading: true,
                        error: null
                    }
                })
            },

            setDiscountValidation: (validation: Partial<DiscountCode>) => {
                const current = get().discountCode
                if (current) {
                    set({
                        discountCode: {
                            ...current,
                            ...validation,
                            isLoading: false
                        }
                    })
                }
            },

            clearDiscount: () => {
                set({ discountCode: null })
            },

            calculateDiscountAmount: (subtotal: number) => {
                const { discountCode } = get()
                if (!discountCode || !discountCode.valid) return 0

                if (discountCode.discountType === 'percentage') {
                    return Math.round((subtotal * discountCode.discountValue) / 100)
                } else if (discountCode.discountType === 'fixed') {
                    // Fixed amount - convert to pence/cents if needed
                    return discountCode.discountValue * 100
                }

                return 0 // free_addon doesn't affect subtotal directly
            },

            calculateFreeAddonDiscount: (basketItems: IBasketItem[]) => {
                const { discountCode } = get()
                if (!discountCode || !discountCode.valid || discountCode.discountType !== 'free_addon') return 0

                let totalFreeAddonValue = 0

                basketItems.forEach(basketItem => {
                    const itemQuantity = basketItem.quantity ?? 1
                    basketItem.extras.forEach((addon) => {
                        const isAddonFree = get().isAddonFree(addon._id, addon.productCatalogueId)
                        if (isAddonFree) {
                            totalFreeAddonValue += (addon.price?.unit_amount ?? 0) * itemQuantity
                        }
                    })
                })

                return totalFreeAddonValue
            },

            calculateFreeAddonSavings: (basketItems: IBasketItem[]) => {
                const { discountCode } = get()
                if (!discountCode || !discountCode.valid || discountCode.discountType !== 'free_addon') return 0

                let totalSavings = 0

                basketItems.forEach(basketItem => {
                    const itemQuantity = basketItem.quantity ?? 1
                    basketItem.extras.forEach((addon) => {
                        const isAddonFree = get().isAddonFree(addon._id, addon.productCatalogueId)
                        if (isAddonFree) {
                            // For free addons, the savings is the original price
                            // Since basket items store original prices, we can use the addon price directly
                            totalSavings += (addon.price?.unit_amount ?? 0) * itemQuantity
                        }
                    })
                })

                return totalSavings
            },

            isAddonFree: (addonId: string, catalogueId?: string) => {
                const { discountCode } = get()
                if (!discountCode || !discountCode.valid || discountCode.discountType !== 'free_addon') return false

                // Check if specific addon ID is in the free list
                if (discountCode.freeAddonIds?.includes(addonId)) return true

                // Check if addon catalogue type is in the free list (e.g., all engraving products)
                if (catalogueId && discountCode.freeAddonTypes?.includes(catalogueId)) return true

                return false
            }
        }),
        {
            name: 'discount-store', // localStorage key
            // Only persist the discountCode, skip functions
            partialize: (state) => ({ discountCode: state.discountCode })
        }
    )
)

export default useDiscountStore  
